import { mergeApiConfig, mergeHeaders } from './utils/requestConfig';
import {
  type ApiConfig,
  type ApiHeaders,
  type ParametersExceptLast,
  type SearchListingsFilters,
  type SearchListingsOptions,
  type WithRequired,
} from './types';
import { deleteSearchAlertPublic, getSearchAlertForDisplay } from './services/unsubscribeSearchAlert';
import { searchListingsCoordinates } from './services/searchListingsCoordinates/searchListingsCoordinates';
import { searchListings } from './services/searchListings';
import { publicReverseGeocoding } from './services/publicReverseGeocoding/publicReverseGeocoding';
import { publicForwardGeocoding } from './services/publicForwardGeocoding/publicForwardGeocoding';
import { messageChatbot } from './services/messageChatbot/messageChatbot';
import { listingLead } from './services/listingLead/listingLead';
import { getTrackingByShopId } from './services/getTrackingByShopId/getTrackingByShopId';
import { getTrackingByGa4Id } from './services/getTrackingByGa4Id/getTrackingByGa4Id';
import { getSuggestionsByDisplayId } from './services/getSuggestionsByDisplayId/getSuggestionsByDisplayId';
import { getSuggestions } from './services/getSuggestions';
import { getSimilarListings } from './services/getSimilarListings/getSimilarListings';
import { getShopsByIds } from './services/getShopsByIds/getShopsByIds';
import { getShopById } from './services/getShopById/getShopById';
import { getPropertyEnginePlaceDetails } from './services/getPropertyEnginePlaceDetails/getPropertyEnginePlaceDetails';
import { getPlaceSuggestions } from './services/getPlaceSuggestions/getPlaceSuggestions';
import { getLocationDetails } from './services/getLocationDetails/getLocationDetails';
import { getListingById } from './services/getListingById/getListingById';
import { getFuzzyPoint } from './services/getFuzzyPoint';
import { getFilterByAiPrompt } from './services/getFilterByAiPrompt';
import { getFavorites } from './services/getFavorites/getFavorites';
import { getAISearchEnabledOutCountries } from './services/getAISearchEnabledOutCountries';
import { getAgentById } from './services/getAgentById/getAgentById';
import { findAllSearchAlerts } from './services/findAllSearchAlerts/findAllSearchAlerts';
import { editSearchAlert } from './services/editSearchAlert/editSearchAlert';
import { deleteSearchAlert } from './services/deleteSearchAlert/deleteSearchAlert';
import { deleteFavoriteByListingId } from './services/deleteFavoriteByListingId/deleteFavoriteByListingId';
import { deleteAllSearchAlerts } from './services/deleteAllSearchAlerts/deleteAllSearchAlerts';
import { deleteAllFavorites } from './services/deleteAllFavorites/deleteAllFavorites';
import {
  addCustomPlace,
  addCustomPlaceBlacklistEntry,
  addCustomPlaceEv,
  deleteCustomPlace,
  deleteCustomPlaceBlacklistEntry,
  getCustomPlaces,
} from './services/customPlaces/customPlaces';
import { createWhatsappLead } from './services/createWhatsappLead/createWhatsappLead';
import { createSearchAlert } from './services/createSearchAlert/createSearchAlert';
import { createFavorites } from './services/createFavorites/createFavorites';

export class SearchApi {
  config: WithRequired<ApiConfig, 'environment'>;

  constructor(config: ApiConfig) {
    this.config = { ...config, environment: config.environment || 'prod' };
  }

  /**
   * Create a copy of the current instance with config overrides.
   */
  withConfig = (config: Partial<ApiConfig>) => new SearchApi(mergeApiConfig(this.config, config));
  /**
   * Create a copy of the current instance with the provided headers.
   */
  withHeaders = (headers: ApiHeaders) =>
    this.withConfig({
      headers: mergeHeaders(this.config.headers, headers),
    });

  /**
   * Fetch property listings based on provided filters and options using the `/api/v2/listing/search` endpoint.
   *
   * {@link https://search-docs-frontend-engelvoelkers.vercel.app/frontend/api/services/listings/searchListings Search API Documentation}
   */
  searchListings = (filters: SearchListingsFilters, options: SearchListingsOptions) => searchListings(filters, options, this.config);

  /**
   * Fetch properties coordinates based on provided filters using the `/api/v2/listing/search/geopoints` endpoint.
   *
   * {@link https://search-docs-frontend-engelvoelkers.vercel.app/frontend/api/services/listings/searchListingsCoordinates Search API Documentation}
   */
  searchListingsCoordinates = (...params: ParametersExceptLast<typeof searchListingsCoordinates>) =>
    searchListingsCoordinates(...params, this.config);

  /**
   * Fetch location suggestions based on the provided query and options using the `/api/v2/autosuggestion/suggest` endpoint.
   *
   * {@link https://search-docs-frontend-engelvoelkers.vercel.app/frontend/api/services/autosuggestion/getPlaceSuggestions Search API Documentation}
   */
  getPlaceSuggestions = (...params: ParametersExceptLast<typeof getPlaceSuggestions>) => getPlaceSuggestions(...params, this.config);

  /**
   * Fetch detailed property information by its listing ID (UUID) using the `/api/v2/listing/{listingId}` endpoint.
   *
   * {@link https://search-docs-frontend-engelvoelkers.vercel.app/frontend/api/services/listings/getListingById Search API Documentation}
   */
  getListingById = (...params: ParametersExceptLast<typeof getListingById>) => getListingById(...params, this.config);

  /**
   * Fetch property listings that are similar to a given listing using the `/api/v1/listing/search/minified` endpoint.
   *
   * {@link https://search-docs-frontend-engelvoelkers.vercel.app/frontend/api/services/listings/getSimilarListings Search API Documentation}
   */
  getSimilarListings = (...params: ParametersExceptLast<typeof getSimilarListings>) => getSimilarListings(...params, this.config);

  /**
   * Fetch either property listing suggestions or place suggestions based on the provided query using either the `/api/v2/autosuggestion/listing/suggest` or `/api/v2/autosuggestion/suggest` endpoint.
   *
   * {@link https://search-docs-frontend-engelvoelkers.vercel.app/frontend/api/services/autosuggestion/getSuggestions Search API Documentation}
   */
  getSuggestions = (...params: ParametersExceptLast<typeof getSuggestions>) => getSuggestions(...params, this.config);

  /**
   * Fetch suggestions based on a partial or full display ID using the `/api/v2/autosuggestion/listing/suggest` endpoint.
   *
   * {@link https://search-docs-frontend-engelvoelkers.vercel.app/frontend/api/services/autosuggestion/getSuggestionsByDisplayId Search API Documentation}
   */
  getSuggestionsByDisplayId = (...params: ParametersExceptLast<typeof getSuggestionsByDisplayId>) =>
    getSuggestionsByDisplayId(...params, this.config);

  /**
   * Fetch detailed location information for a given place ID using the `/api/v2/autosuggestion/details` endpoint.
   *
   * {@link https://search-docs-frontend-engelvoelkers.vercel.app/frontend/api/services/autosuggestion/getLocationDetails Search API Documentation}
   */
  getLocationDetails = (...params: ParametersExceptLast<typeof getLocationDetails>) => getLocationDetails(...params, this.config);

  /**
   * Fetch detailed location information for a given place ID using the `/api/v1/autosuggestion/property-engine/details` endpoint.
   *
   * {@link https://search-docs-frontend-engelvoelkers.vercel.app/frontend/api/services/autosuggestion/getPropertyEnginePlaceDetails Search API Documentation}
   */
  getPropertyEnginePlaceDetails = (...params: ParametersExceptLast<typeof getPropertyEnginePlaceDetails>) =>
    getPropertyEnginePlaceDetails(...params, this.config);

  /**
   * Fetch custom places using the `/api/v1/autosuggestion/data/custom-places` endpoint.
   */
  getCustomPlaces = (...params: ParametersExceptLast<typeof getCustomPlaces>) => getCustomPlaces(...params, this.config);

  /**
   * Create a Google custom place using the `/api/v1/autosuggestion/data/custom-places/google` endpoint.
   */
  addCustomPlace = (...params: ParametersExceptLast<typeof addCustomPlace>) => addCustomPlace(...params, this.config);

  /**
   * Create an EV custom place using the `/api/v1/autosuggestion/data/custom-places/ev` endpoint.
   */
  addCustomPlaceEv = (...params: ParametersExceptLast<typeof addCustomPlaceEv>) => addCustomPlaceEv(...params, this.config);

  /**
   * Create a custom place blacklist entry using the `/api/v1/autosuggestion/data/custom-places/blacklist` endpoint.
   */
  addCustomPlaceBlacklistEntry = (...params: ParametersExceptLast<typeof addCustomPlaceBlacklistEntry>) =>
    addCustomPlaceBlacklistEntry(...params, this.config);

  /**
   * Delete a custom place using the `/api/v1/autosuggestion/data/custom-places/{placeId}` endpoint.
   */
  deleteCustomPlace = (...params: ParametersExceptLast<typeof deleteCustomPlace>) => deleteCustomPlace(...params, this.config);

  /**
   * Delete a custom place blacklist entry using the `/api/v1/autosuggestion/data/custom-places/blacklist/{placeId}` endpoint.
   */
  deleteCustomPlaceBlacklistEntry = (...params: ParametersExceptLast<typeof deleteCustomPlaceBlacklistEntry>) =>
    deleteCustomPlaceBlacklistEntry(...params, this.config);

  /**
   * Create a search alert using the `/api/v4/searchalert/alert` endpoint.
   *
   * {@link https://search-docs-frontend-engelvoelkers.vercel.app/frontend/api/services/searchAlerts/createSearchAlert Search API Documentation}
   */
  createSearchAlert = (...params: ParametersExceptLast<typeof createSearchAlert>) => createSearchAlert(...params, this.config);

  /**
   * Delete all search alerts of an Account using the `/api/v4/searchalert/alert` endpoint.
   *
   * {@link https://search-docs-frontend-engelvoelkers.vercel.app/frontend/api/services/searchAlerts/deleteAllSearchAlert Search API Documentation}
   */
  deleteAllSearchAlerts = (...params: ParametersExceptLast<typeof deleteAllSearchAlerts>) => deleteAllSearchAlerts(...params, this.config);

  /**
   * Delete a search alert using the `/api/v4/searchalert/alert/{searchAlertId}` endpoint.
   *
   * {@link https://search-docs-frontend-engelvoelkers.vercel.app/frontend/api/services/searchAlerts/deleteSearchAlert Search API Documentation}
   */
  deleteSearchAlert = async (...params: ParametersExceptLast<typeof deleteSearchAlert>) => deleteSearchAlert(...params, this.config);

  /**
   * Edit a search alert using the `/api/v4/searchalert/alert/{searchAlertId}` endpoint.
   *
   * {@link https://search-docs-frontend-engelvoelkers.vercel.app/frontend/api/services/searchAlerts/editSearchAlert Search API Documentation}
   */
  editSearchAlert = (...params: ParametersExceptLast<typeof editSearchAlert>) => editSearchAlert(...params, this.config);

  /**
   * Find all search alerts of an account using the `/api/v4/searchalert/alert` endpoint.
   *
   * {@link https://search-docs-frontend-engelvoelkers.vercel.app/frontend/api/services/searchAlerts/findAllSearchAlerts Search API Documentation}
   */
  findAllSearchAlerts = (...params: ParametersExceptLast<typeof findAllSearchAlerts>) => findAllSearchAlerts(...params, this.config);

  /**
   * Returns all tracking ids based on a given google GA4 id using the `/api/v2/tracking/ga4/{ga4Id}` endpoint.
   *
   * {@link https://search-docs-frontend-engelvoelkers.vercel.app/frontend/api/services/tracking/getTrackingByGa4Id Search API Documentation}
   */
  getTrackingByGa4Id = (...params: ParametersExceptLast<typeof getTrackingByGa4Id>) => getTrackingByGa4Id(...params, this.config);

  /**
   * Returns all tracking ids based on a given master data shop id using the `/api/v2/tracking/shop/{masterDataShopId}` endpoint.
   *
   * {@link https://search-docs-frontend-engelvoelkers.vercel.app/frontend/api/services/tracking/getTrackingByShopId Search API Documentation}
   */
  getTrackingByShopId = (...params: ParametersExceptLast<typeof getTrackingByShopId>) => getTrackingByShopId(...params, this.config);

  /**
   * Lead contact form to request further information, viewing, callback, etc. for a certain listing using the `/api/v3/listing/{listingID}/lead` endpoint.
   *
   * {@link https://search-docs-frontend-engelvoelkers.vercel.app/frontend/api/services/leads/listingLead Search API Documentation}
   */
  listingLead = (...params: ParametersExceptLast<typeof listingLead>) => listingLead(...params, this.config);

  /**
   * Creates a WhatsApp lead and returns the wa.me redirect URL using the `/api/v1/listing/{listingId}/lead/whatsapp` endpoint.
   */
  createWhatsappLead = (...params: ParametersExceptLast<typeof createWhatsappLead>) => createWhatsappLead(...params, this.config);

  /**
   * Fetch fuzzy point information based on the original point provided using the `/api/v1/geocode/fuzzy/point` endpoint.
   *
   * {@link https://search-docs-frontend-engelvoelkers.vercel.app/frontend/api/services/geocoding/getFuzzyPoint Search API Documentation}
   */
  getFuzzyPoint = (...params: ParametersExceptLast<typeof getFuzzyPoint>) => getFuzzyPoint(...params, this.config);

  /**
   * Fetch favorites for an account using the `/api/v1/favorites/account/{accountId}/listings` endpoint.
   *
   * {@link https://search-docs-frontend-engelvoelkers.vercel.app/frontend/api/services/favorites/getFavorites Search API Documentation}
   */
  getFavorites = (...params: ParametersExceptLast<typeof getFavorites>) => getFavorites(...params, this.config);
  /**
   * Create favorites for a given account using the `/api/v1/favorites/account/{accountId}/listings` endpoint.
   *
   * {@link https://search-docs-frontend-engelvoelkers.vercel.app/frontend/api/services/favorites/createFavorites Search API Documentation}
   */
  createFavorites = (...params: ParametersExceptLast<typeof createFavorites>) => createFavorites(...params, this.config);

  /**
   * Delete all favorites for an account using the `/api/v1/favorites/account/{accountId}/listings` endpoint.
   *
   * {@link https://search-docs-frontend-engelvoelkers.vercel.app/frontend/api/services/favorites/deleteAllFavorites Search API Documentation}
   */
  deleteAllFavorites = (...params: ParametersExceptLast<typeof deleteAllFavorites>) => deleteAllFavorites(...params, this.config);

  /**
   * Delete a property from the Favorites list for an account using the `/api/v1/favorites/account/{accountId}/listing/{listingId}` endpoint.
   *
   * {@link https://search-docs-frontend-engelvoelkers.vercel.app/frontend/api/services/favorites/deleteFavoriteByListingId Search API Documentation}
   */
  deleteFavoriteByListingId = (...params: ParametersExceptLast<typeof deleteFavoriteByListingId>) =>
    deleteFavoriteByListingId(...params, this.config);

  /**
   * Fetch public forward geocoding using the `/api/v2/public/geocode/forward` endpoint.
   *
   * {@link https://search-docs-frontend-engelvoelkers.vercel.app/frontend/api/services/geocoding/publicForwardGeocoding Search API Documentation}
   */
  publicForwardGeocoding = (...params: ParametersExceptLast<typeof publicForwardGeocoding>) =>
    publicForwardGeocoding(...params, this.config);

  /**
   * Fetch public reverse geocoding using the `/api/v2/public/geocode/reverse` endpoint.
   *
   * {@link https://search-docs-frontend-engelvoelkers.vercel.app/frontend/api/services/geocoding/publicReverseGeocoding Search API Documentation}
   */
  publicReverseGeocoding = (...params: ParametersExceptLast<typeof publicReverseGeocoding>) =>
    publicReverseGeocoding(...params, this.config);

  /**
   * Fetch the agent details based on a given agent id using the `/api/v1/masterdata/agent/{agentId}` endpoint.
   *
   * {@link https://search-docs-frontend-engelvoelkers.vercel.app/frontend/api/services/agent/getAgentById Search API Documentation}
   */
  getAgentById = (...params: ParametersExceptLast<typeof getAgentById>) => getAgentById(...params, this.config);

  /**
   * Fetch shop details by master data id or short id using the `/api/v1/masterdata/shop/{idOrShortId}` endpoint.
   *
   * {@link https://search-docs-frontend-engelvoelkers.vercel.app/frontend/api/services/shop/getShopById Search API Documentation}
   */
  getShopById = (...params: ParametersExceptLast<typeof getShopById>) => getShopById(...params, this.config);

  /**
   * Fetch shop details for multiple master data ids or short ids using the `/api/v1/masterdata/shop` endpoint.
   *
   * {@link https://search-docs-frontend-engelvoelkers.vercel.app/frontend/api/services/shop/getShopsByIds Search API Documentation}
   */
  getShopsByIds = (...params: ParametersExceptLast<typeof getShopsByIds>) => getShopsByIds(...params, this.config);

  /**
   * Get search alert information for deletion dialog (public endpoint) using the `/api/v4/searchalert/unauth/alert/{searchAlertId}` endpoint.
   *
   * {@link https://search-docs-frontend-engelvoelkers.vercel.app/frontend/api/services/searchAlerts/getSearchAlertForDisplay Search API Documentation}
   */
  getSearchAlertForDisplay = (...params: ParametersExceptLast<typeof getSearchAlertForDisplay>) =>
    getSearchAlertForDisplay(...params, this.config);

  /**
   * Delete a search alert (public endpoint) using the `/api/v4/searchalert/unauth/alert/{searchAlertId}` endpoint.
   *
   * {@link https://search-docs-frontend-engelvoelkers.vercel.app/frontend/api/services/searchAlerts/deleteSearchAlertPublic Search API Documentation}
   */
  deleteSearchAlertPublic = (...params: ParametersExceptLast<typeof deleteSearchAlertPublic>) =>
    deleteSearchAlertPublic(...params, this.config);

  /**
   * Fetches the countries for which the BFF already supports AI search using the `/api/v1/ai/rolled-out-countries` endpoint.
   *
   * {@link https://search-docs-frontend-engelvoelkers.vercel.app/frontend/api/services/ai/getAISearchEnabledOutCountries Search API Documentation}
   */
  getAISearchEnabledOutCountries = (...params: ParametersExceptLast<typeof getAISearchEnabledOutCountries>) =>
    getAISearchEnabledOutCountries(...params, this.config);

  /**
   * Converts a text to a search filter using the `/api/v1/ai/text-to-filter` endpoint.
   *
   * {@link https://search-docs-frontend-engelvoelkers.vercel.app/frontend/api/services/ai/getFilterByAiPrompt Search API Documentation}
   */
  getFilterByAiPrompt = (...params: ParametersExceptLast<typeof getFilterByAiPrompt>) => getFilterByAiPrompt(...params, this.config);

  /**
   * Send a message to the chatbot using the `/api/v1/ai-chatbot/message` endpoint.
   *
   * {@link https://search-docs-frontend-engelvoelkers.vercel.app/frontend/api/services/chatbot/messageChatbot Search API Documentation}
   */
  messageChatbot = (...params: ParametersExceptLast<typeof messageChatbot>) => messageChatbot(...params, this.config);
}
